import * as Sentry from '@sentry/react';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn('[sentry] VITE_SENTRY_DSN missing — Sentry disabled');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
    replaysSessionSampleRate: Number(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.0,
    ),
    replaysOnErrorSampleRate: Number(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 1.0,
    ),
  });

  // Expose debug helpers on window so you can verify the wiring from the
  // browser console: `__sentryTestError()` / `__sentryTestMessage()`.
  // Drop these if/when Sentry coverage is verified in CI.
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).__sentryTestError = () => {
      // Two paths in case one fails:
      // 1. Explicit captureException — always sends, doesn't depend on globalHandlers.
      // 2. setTimeout throw — reaches window.onerror so we also exercise the global handler.
      // Throwing directly from the DevTools console doesn't always trigger window.onerror;
      // Chrome may tag console-origin errors as DevTools-internal and skip handlers.
      const err = new Error('Sentry frontend test error — ' + new Date().toISOString());
      const id = Sentry.captureException(err);
      console.log('[sentry] captureException eventId:', id);
      setTimeout(() => {
        throw new Error('Sentry frontend test async error — ' + new Date().toISOString());
      }, 0);
      return id;
    };
    (window as unknown as Record<string, unknown>).__sentryTestMessage = () => {
      const id = Sentry.captureMessage(
        'Sentry frontend test message — ' + new Date().toISOString(),
        'info',
      );
      console.log('[sentry] captureMessage eventId:', id);
      return id;
    };
  }
}

export { Sentry };
